import { takeLatest, call, put, all } from 'redux-saga/effects';
import { get } from 'lodash-es';
import apiCall from '../../utils/apiCall';
import axios from '../../utils/axios';
import { requestFail, requestPending, requestSuccess } from '../../utils/status';
import {
  aggregateBuildingPaymentStatsFromHistory,
  getMonthUtcRangeIso
} from '../../views/payments/PaymentUtils';
import {
  ADD_PAYMENT_METHOD,
  CANCEL_SCHEDULED_TRANSFER,
  REFUND_VOPAY_PAYMENT,
  CREATE_RESIDENT_VOPAY_ACCOUNT,
  FUND_TRANSFER_WITHDRAW,
  FUND_TRANSFER_WITHDRAW_SCHEDULE,
  GET_BUILDING_ONBOARDING_STATUS,
  GET_VOPAY_USER,
  GET_VOPAY_USER_PAYMENT_METHODS,
  GET_ORGANIZATION_ONBOARDING_STATUS,
  GET_BUILDING_PAYMENT_STATS,
  SYNC_PAYMENT_METHOD
} from '../constants';

function* getBuildingPaymentStats(action) {
  const {
    buildingID,
    month,
    year,
    success: successPayload,
    fail: failPayload,
    finally: finallyPayload
  } = action.payload || {};
  const idToken = localStorage.getItem('idToken');
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${idToken}`
  };

  try {
    yield put({ type: requestPending(GET_BUILDING_PAYMENT_STATS) });

    const { startDate, endDate } = getMonthUtcRangeIso(month, year);

    const paymentsPath = `/vopay/buildings/${buildingID}/payments`;
    const scheduledPath = `/vopay/buildings/${buildingID}/scheduled-payments`;
    const params = { start_date: startDate, end_date: endDate };

    const [paymentsRes, scheduledRes] = yield all([
      call(axios.request, { url: paymentsPath, method: 'GET', headers, params }),
      call(axios.request, { url: scheduledPath, method: 'GET', headers, params })
    ]);

    const payload = aggregateBuildingPaymentStatsFromHistory(
      paymentsRes.data,
      scheduledRes.data
    );

    yield put({
      type: requestSuccess(GET_BUILDING_PAYMENT_STATS),
      payload
    });
    successPayload && successPayload({ data: payload });
  } catch (err) {
    const errRes = get(err, 'response', err);
    yield put({
      type: requestFail(GET_BUILDING_PAYMENT_STATS),
      payload: errRes
    });
    failPayload && failPayload(errRes);
  } finally {
    finallyPayload && finallyPayload();
  }
}

const getVopayUser = apiCall({
  type: GET_VOPAY_USER,
  method: 'GET',
  path: ({ userId, buildingID }) => {
    return `/vopay/buildings/${buildingID}/user/${userId}`;
  }
});

const getVopayUserPaymentMethods = apiCall({
  type: GET_VOPAY_USER_PAYMENT_METHODS,
  method: 'GET',
  path: ({ userId }) => `/vopay/users/${userId}/payment-methods`
});

const getOrganizationOnboardingStatus = apiCall({
  type: GET_ORGANIZATION_ONBOARDING_STATUS,
  method: 'GET',
  path: ({ id }) => {
    return `/vopay/organizations/${id}/onboarding-status`;
  }
});

const getBuildingOnboardingStatus = apiCall({
  type: GET_BUILDING_ONBOARDING_STATUS,
  method: 'GET',
  path: ({ id }) => {
    return `/vopay/buildings/${id}/onboarding-status`;
  }
});

const createResidentAccount = apiCall({
  type: CREATE_RESIDENT_VOPAY_ACCOUNT,
  method: 'POST',
  path: () => {
    return `/vopay/account/user`;
  }
});

const addPaymentMethod = apiCall({
  type: ADD_PAYMENT_METHOD,
  method: 'POST',
  path: () => {
    return `/vopay/payment-methods`;
  }
});

const syncPaymentMethod = apiCall({
  type: SYNC_PAYMENT_METHOD,
  method: 'POST',
  path: () => {
    return `/vopay/payment-methods/sync`;
  }
});

const makeAPayment = apiCall({
  type: FUND_TRANSFER_WITHDRAW,
  method: 'POST',
  path: () => {
    return `/vopay/payments`;
  }
});

const scheduleAPayment = apiCall({
  type: FUND_TRANSFER_WITHDRAW_SCHEDULE,
  method: 'POST',
  path: () => {
    return `/vopay/scheduled-payments`;
  }
});

const cancelAScheduledPayment = apiCall({
  type: CANCEL_SCHEDULED_TRANSFER,
  method: 'POST',
  path: () => {
    return `/vopay/scheduled-payments/cancel`;
  }
});

const refundVopayPaymentCall = apiCall({
  type: REFUND_VOPAY_PAYMENT,
  method: 'POST',
  path: () => `/vopay/payments/refund`
});

export default function* paymentsSaga() {
  yield takeLatest(GET_BUILDING_PAYMENT_STATS, getBuildingPaymentStats);
  yield takeLatest(GET_ORGANIZATION_ONBOARDING_STATUS, getOrganizationOnboardingStatus);
  yield takeLatest(GET_BUILDING_ONBOARDING_STATUS, getBuildingOnboardingStatus);
  yield takeLatest(GET_VOPAY_USER, getVopayUser);
  yield takeLatest(GET_VOPAY_USER_PAYMENT_METHODS, getVopayUserPaymentMethods);
  yield takeLatest(CREATE_RESIDENT_VOPAY_ACCOUNT, createResidentAccount);
  yield takeLatest(ADD_PAYMENT_METHOD, addPaymentMethod);
  yield takeLatest(SYNC_PAYMENT_METHOD, syncPaymentMethod);

  yield takeLatest(FUND_TRANSFER_WITHDRAW, makeAPayment);
  yield takeLatest(FUND_TRANSFER_WITHDRAW_SCHEDULE, scheduleAPayment);
  yield takeLatest(CANCEL_SCHEDULED_TRANSFER, cancelAScheduledPayment);
  yield takeLatest(REFUND_VOPAY_PAYMENT, refundVopayPaymentCall);
}
